<template>
    <div class="2xl:overflow-hidden relative container">
        <div class="grid grid-cols-1 xl:grid-cols-2 px-4 py-10 2xl:py-[50px]">
            <div class="max-w-[390px] flex flex-col lg:mb-0 mb-4 justify-center mx-auto">
                <h2>
                    {{ data.tag?.toUpperCase() }}
                </h2>
                <h2
                    class="text-title-lg md:text-display-md font-bold text-gray-100 tracking-tight max-w-[365px] text-center 2xl:text-start"
                >
                    {{ data.title }}
                    <span class="text-orange-400">{{ data.title_span }}</span>
                </h2>
                <div class="my-4 md:my-6">
                    <div v-for="feature in data.features">
                        <div class="flex space-x-2 space-y-2 mb-2">
                            <div class="bg-[#7777CB33] p-1 md:p-[6px] size-fit rounded-full flex justify-center">
                                <Icon class="text-brand-400 size-6" :name="`tabler:${feature.icon}`" />
                            </div>
                            <p class="text-body-sm md:text-body-md text-brand-200 leading-5">{{ feature.text }}</p>
                        </div>
                    </div>
                </div>

                <AppButton
                    :id="data.cta.id"
                    @click="useRouter().push(data.cta.link)"
                    class="h-12 !w-fit mx-auto 2xl:ml-0"
                    block
                >
                    <div class="flex items-center text-body-md font-medium whitespace-nowrap space-x-1">
                        <Icon v-if="!auth" class="size-6" name="tabler:upload" />
                        <p>{{ auth ? $t("global_letsGo") : data.cta.text }}</p>
                    </div>
                </AppButton>
            </div>
            <ClientOnly v-if="useDevice().isMobile">
                <template #default>
                    <div class="h-[500px] 2xl:h-[610px]">
                        <AppVSSliderHero :slide="data.slides[0]" />
                    </div>
                </template>
                <template #fallback>
                    <div class="h-[500px] 2xl:h-[610px]">
                        <AppVSSliderHero :slide="data.slides[0]" />
                    </div>
                </template>
                
            </ClientOnly>
            <ClientOnly v-else>
                <template #default>
                    <div class="relative" @touchstart="pauseAutoplay" @touchend="resumeAutoplay">
                        <Swiper
                            @swiper="onSwiper"
                            :slides-per-view="1"
                            :effect="'fade'"
                            :autoplay="{
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }"
                            @reach-end="
                                () => {
                                    activeSlideIndex = 0;
                                }
                            "
                            :navigation="false"
                            :modules="[SwiperAutoplay, SwiperEffectFade]"
                            @autoplayTimeLeft="onAutoplayTimeLeft"
                            @slide-change="sliderChangeWatch"
                            class="mySwiper"
                        >
                            <SwiperSlide v-for="(card, cardIndex) in data.slides">
                                <div class="h-[500px] 2xl:h-[610px]">
                                    <AppVSSliderHero :slide="card" />
                                </div>
                            </SwiperSlide>
                            <template #container-start>
                                <div
                                    class="2xl:p-4 p-2 absolute z-50 -bottom-5 2xl:bottom-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full h-12 flex"
                                >
                                    <div class="flex justify-between items-center space-x-4">
                                        <AppButton
                                            variant="ghost"
                                            @click="previousSlide()"
                                            class="w-8 h-8 bg-transparent rounded-full flex justify-center items-center cursor-pointer"
                                        >
                                            <Icon name="tabler:chevron-left" class="size-4 text-gray-100" />
                                        </AppButton>
                                        <div class="flex space-x-2">
                                            <div v-for="(slide, index) in data.slides?.length" class="flex space-x-2">
                                                <div
                                                    v-if="index !== activeSlideIndex"
                                                    @click="goToSlide(index)"
                                                    class="w-2 h-2 bg-[#FFFFFF33] rounded-full cursor-pointer"
                                                ></div>
                                                <div v-else class="autoplay-progress">
                                                    <div class="bg-[#FFFFFF33] w-14 h-2 rounded-full relative">
                                                        <div
                                                            ref="progressCircle"
                                                            :style="`width: ${properWidth}`"
                                                            class="w-0 h-2 rounded-full bg-gray-400"
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <AppButton
                                            variant="ghost"
                                            @click="nextSlide()"
                                            class="w-8 h-8 bg-transparent rounded-full flex justify-center items-center cursor-pointer"
                                        >
                                            <Icon name="tabler:chevron-right" class="size-4 text-gray-100" />
                                        </AppButton>
                                    </div>
                                </div>
                            </template>
                        </Swiper>
                    </div>
                </template>
                <template #fallback>
                    <div class="h-[500px] 2xl:h-[610px]">
                        <AppVSSliderHero :slide="data.slides[0]" />
                    </div>
                </template>
            </ClientOnly>
        </div>
        <div
            class="bg-brand-500 hidden 2xl:block w-[1000px] h-[700px] rounded-full blur-[100px] opacity-70 absolute -bottom-[600px] left-1/2 -translate-x-1/2"
        ></div>
    </div>
</template>
<script setup lang="ts">
import type { IVSHero } from "~/types/components/landings.type";

interface Props {
    data: IVSHero;
}
const auth = useAuth();
const props = defineProps<Props>();
const key = ref(0);
const activeSlideIndex = ref(-1);
const slider = ref<{ slideTo: any; slidePrev: any; slideNext: any; activeIndex: number; autoplay: any } | null>(null);

onMounted(() => {
    activeSlideIndex.value = 0;
});
const goToSlide = (slideIndex: number) => {
    if (activeSlideIndex.value == slideIndex) return;
    activeSlideIndex.value = slideIndex;
    slider.value?.slideTo(slideIndex);
    slider.value!.activeIndex = slideIndex;
};

const onSwiper = (swiper: any) => {
    slider.value = swiper;
};

const sliderChangeWatch = () => {
    activeSlideIndex.value = slider.value?.activeIndex as number;
    key.value++;
};

const progressCircle = ref(null);
const properWidth = ref();
const onAutoplayTimeLeft = (s: any, time: number, progress: number) => {
    properWidth.value = progress * 100 + "%";
};
const previousSlide = () => {
    if (activeSlideIndex.value === 0) {
        goToSlide(props.data.slides.length);
    } else {
        activeSlideIndex.value--;
        slider.value!.slidePrev();
    }
};
const route = useRoute();

const pauseAutoplay = () => {
    slider.value!.autoplay.pause();
};
const resumeAutoplay = () => {
    slider.value!.autoplay.resume();
};
watch(
    route,
    () => {
        activeSlideIndex.value = 0;
    },
    { deep: true }
);

const nextSlide = () => {
    if (activeSlideIndex.value + 1 === props.data.slides.length) {
        goToSlide(0);
    } else {
        activeSlideIndex.value++;
        slider?.value?.slideNext();
    }
};
</script>

<style scoped>
.autoplay-progress {
    position: relative;
}

.autoplay-progress > div > div {
    position: absolute;
    right: 0;
    top: 0px;
    z-index: 10;
    height: 100%;
}
</style>
